// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";
import cx from "classnames";

// interfaces
import { PaymentMethodDef } from "@payment-ui/interfaces";

// widgets
import { PaymentMethodIcon } from "@payment-ui/widgets/payment-method-icon";

// helpers
import {
    getPaymentMethodIconUrl,
    getPaymentMethodDisplayName,
    getPaymentMethodDiscount,
    getPaymentMethodPromoMsg,
} from "@payment-ui/helpers/Payment";

// testids
import { IconListViewTestIdsType } from "@swiggy-private/payment-test-ids";

import styles from "./IconListView.scss";

import { ArrowSvgIcon } from "@payment-ui/widgets/arrow-svg-icon";
import { PaymentMethodName } from "@payment-ui/widgets/payment-method-name";
export interface IconListViewProps {
    methodData: PaymentMethodDef;
    onClick: VoidFunction;
    isMethodEnabled: boolean;
    testIds: IconListViewTestIdsType;
}

function IconListView(props: IconListViewProps): ReactElement {
    const { methodData, onClick, isMethodEnabled, testIds } = props;
    const iconUrl = useMemo(() => getPaymentMethodIconUrl(methodData), [methodData]);
    const displayName = useMemo(() => getPaymentMethodDisplayName(methodData), [methodData]);
    const discount = useMemo(() => getPaymentMethodDiscount(methodData), [methodData]);
    const promoMsg = useMemo(() => getPaymentMethodPromoMsg(methodData), [methodData]);
    const containerClass = useMemo(
        () =>
            cx(styles.paymentMethod, {
                [styles.paymentMethodDisabled]: !isMethodEnabled,
            }),
        [isMethodEnabled],
    );

    const onContainerClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (!isMethodEnabled) {
                return;
            }
            onClick();
        },
        [isMethodEnabled, onClick],
    );

    return (
        <div className={containerClass} onClick={onContainerClick} data-testid={testIds.CONTAINER}>
            <div className={styles.paymentMethodContainer}>
                <PaymentMethodIcon
                    src={iconUrl}
                    data-testid={testIds.ICON}
                    parentClassName={styles.paymentMethodIconParent}
                />
                <PaymentMethodName
                    displayName={displayName}
                    enabled={isMethodEnabled}
                    discount={discount}
                    data-testid={testIds.NAME}
                    promoMsg={promoMsg}
                />
                <ArrowSvgIcon className={styles.arrowIcon} />
            </div>
        </div>
    );
}

export default memo(IconListView);
